import { css, html, nothing, PropertyValueMap } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { customElement, property, state } from 'lit/decorators.js';
import { getFreeTrialDuration } from '../free-trial/utils/free-trial.util';
import { getUserDataForUserAnalytics } from '../user-profile/user-profile.selectors';
import { AlertSize } from '../alert/alert';
import {
  calculateDurationDigitalProduct,
  DEFAULT_COVER_IMAGE,
  getImageSrcUrl,
  getProductTitleWithoutEdition,
  isConnectProduct,
  isSharpenPlus,
} from '../../common/utils/product-util';
import {
  formatDateDefault,
  formattedJoin,
  escapeRegExp,
  isTextContainsHTMLTags,
  isTunnelPurchaseFlow,
  ordinalSuffixOf,
  PROFESSIONAL_LOWER,
  truncatingCleanString,
} from '../../common/utils/util';
import {
  cardAlert,
  renderItemPrice,
  renderOriginalPrice,
  renderPrice,
  renderQuantity,
} from '../../common/utils/product-card-util';
import { selectRequest, selectSelectedCatalog } from '../search/store/search.selector';
import { BaseComponent } from '../base-component/base-component';
import { StoreState } from '../../store/store.interface';
import { mapProductToFreeTrialInfo } from '../free-trial/mappings/free-trial-info.mapping';

import heading from '../../common/headings';
import store from '../../store/store';
import emit from '../../common/events';
import '../cart/add-to-cart';
import '../alert/alert';
import { isCustomerLoginIdleOrLoading, isCustomerLoginSuccess } from '../customer/customer';
import assignmentIcon from '../../common/icons/assignment-icon';
import { selectV2Request, selectTotal } from '../search-v2/store/search-v2.selectors';
import '../free-trial/signedin-availability-free-trial.display';
import '../free-trial/signedout-availability-free-trial.display';
import '../my-bookshelf/my-bookshelf-redeem-link';
import '../create-custom/create-custom-product-card-display';
import { facetsMapping, getNbspFacetLabels } from '../../common/utils/search-v2-util';
import { displayFreeTrialMessages } from '../free-trial/utils/free-trial-messages.util';
import { ElasticAnalyticsService, IElasticAnalyticsService } from './services/elastic-cloud-analytics.service';
import { ElasticAnalyticsAPI } from './apis/elastic-cloud-analytics.api';
import { BreadCrumbItem } from '../search/interfaces/search.interface';
import { trackSearchClickthrough } from '../search/utils/search.util';
import {
  productDiscoveryQPKey,
  productDiscoveryQPValue,
} from '../../common/constants/anayltics.constants';
import { appendQueryParamToUrl } from '../../common/utils/analytics-util';
import { OfferTypeChecker } from '../../common/utils/offer-type-util';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import {
  CreateCoverImage,
  CreateCustomProductSupplement,
} from '../create-custom/interfaces/create-custom-product.interface';
import {
  dataLayerDetail,
  getSanitizedProductTitle,
  hasAuthorInfo,
  isRedeemableProduct,
  populatePdpLocalStorageObject,
  renderPdpSmallCardTemplate,
} from './utils/product-card.util';
import { MetaOfferContentsMetadata } from '../cart/shop-cart/interfaces/shop-cart.interface';

/**
 * This is used to abstract the APIs from ecomm-ui. This component declares it's own JSON interface
 * that other components need to respect, so that when the underlying APIs are changed the interface
 * of this component does not change.
 */
export declare interface ProductCardData {
  kind: 'ProductCardData';
  title: string;
  image: string;
  url: string;
  copyRightYear: string;
  businessUnit: string;
  productBusinessUnit?: string;
  instructorOnly: boolean;
  store?: string;
  isbn: string | undefined;
  isbn13: string | undefined;
  contributors: Array<string> | undefined;
  description: string;
  gradeLevels: string | undefined;
  isPublished?: boolean;
  edition?: number;
  editionOrRelease?: string;
  totalPrice?: number;
  listPrice?: number;
  netPrice: number;
  format?: string;
  titleType?: string | null;
  isVirtual?: boolean;
  cardType?: string;
  quantity?: number;
  mhid?: string;
  errorCode?: string;
  errorMessage?: string;
  looseLeafAvailable?: boolean;
  hasEbook?: boolean;
  isConnectSharpenBundle?: boolean;
  isSharpenCompanion?: boolean;
  isSharpenAdvantage?: boolean;
  term?: string;
  countryCode?: string;
  currencyCode?: string;
  catalogLabel?: string;
  documentType?: string;
  type?: string;
  publicationDate?: string;
  duration?: number;
  durationPeriod?: string;
  isEvergreen?: boolean;
  offerType?: string;
  offerBranding?: string;
  application?: string;
  itemType?: string;
  flow?: string;
  isFreeTrial?: boolean;
  isFreeTrialReplaced?: boolean;
  isConvertedFromFreeTrial?: boolean;
  freeTrialEndDate?: string;
  freeTrialStartDate?: string;
  freeTrialSubscriptionAmount?: number;
  freeTrialSubscriptionUnitPrice?: number;
  partsOf?: BreadCrumbItem;
  programId?: string;
  programSourceId?: string;
  programSourceName?: string;
  comments?: string;
  userName?: string;
  programLabel?: string;
  optimizedUniqueTitle: string;
  offerPricingId?: string;
  id: string;
  bundleDiscountedPrice: number;
  isDiscountedBundle: boolean;
  formatTypeDesc?: string;
  relatedProducts?: string[];
  durationType?: string;
  courseNumber?: string;
  createCoverImages?: CreateCoverImage[];
  department?: string;
  instructorNames?: string[];
  schoolName?: string;
  supplements?: CreateCustomProductSupplement[];
  metaOfferContentsMetadata?: MetaOfferContentsMetadata[];
  isAleksSharpenCompanionBundle?: boolean;
}

export declare interface ProductSmallCardData {
  kind: 'ProductSmallCardData';
  title: string;
  image: string;
  url: string;
  createdDate: number;
  modifiedDate: number;
  isPublished?: boolean;
  businessUnit?: string;
  mhid?: string;
  isbn?: string;
  documentType?: string;
  catalogLabel?: string;
  isFreeTrial?: boolean;
  offerType?: string;
  offerPricingId?: string;
  freeTrialEndDate?: string;
  freeTrialSubscriptionAmount?: number;
  freeTrialSubscriptionUnitPrice?: number;
  format?: string;
  formatTypeDesc?: string;
  isbn10?: string;
  isbn13?: string;
  programSourceId?: string;
  durationType?: string;
  isEvergreen?: boolean;
  edition?: number;
  editionOrRelease?: string;
  isVirtual?: boolean;
  duration?: number;
  durationPeriod?: string;
  createCoverImages?: CreateCoverImage[];
}

export declare interface SearchResultLargeCardData {
  title: string;
  body: string;
  learning_level: Array<string>;
  businessUnit: string;
  product: Array<string>;
  url: string;
  id: string;
  documentType: string;
  user_type: Array<string>;
  created_by_division: Array<string>;
  tags: Array<string>;
}

/**
 * A static element to display product information.
 *
 */
@customElement('mhecomm-product-card-display')
export class ProductCardDisplay extends BaseComponent {
  static styles = css`
    .small-cart-format-title {
      font-size: 0.875rem;
    }
  `;

  private elasticAnalyticsService: IElasticAnalyticsService;

  constructor() {
    super();
    this.elasticAnalyticsService = new ElasticAnalyticsService(new ElasticAnalyticsAPI());
  }

  /**
   * The heading level of the heading text. This should be h1 - h6.
   * If no value is provided or an improper value is provided, h3 will be used.
   */
  @property()
  headingLevel = 'p';

  /**
   * The variation of the product card to display in different variations or styles, the values can be small-card, large-card.
   */
  @property()
  variation = 'small-card';

  /**
   * The text to display on the add to cart button.
   */
  @property()
  text = 'Add to Cart';

  /**
   * A JSON String in the format of 'ProductCardData'.
   */
  @property({ type: Object })
  product?: ProductCardData | ProductSmallCardData;

  @property({ type: Boolean })
  isCartCard = false;

  @property({type: Boolean })
  hideCoverImage = false;

  /**
   * This property will decide whether search result need to show in List or Card view.
   * Currently this is being used in PLP search result page.
   */
  @property()
  view = '';

  @property()
  store?: string;

  /**
   * A JSON String in the format of 'ProductCardData'.
   */
  @property({ type: Object })
  searchResult?: SearchResultLargeCardData;

  @property({ type: Boolean })
  isSearchResults = false;

  @property({ type: Number })
  productIndex?: number;

  @property({ type: Boolean })
  isCalledFromCartPreview = false;

  @state()
  private errorImage?: string;

  @state()
  private showFullTitle = false;

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(_changedProperties);
    if (_changedProperties.has('product') && this.product && this.product.url && this.isSearchResults) {
      const updatedUrl = appendQueryParamToUrl(this.product.url, productDiscoveryQPKey, productDiscoveryQPValue);
      if (this.product.url !== updatedUrl) {
        this.product = { ...this.product, url: updatedUrl };
      }
    }
    const prevProduct = _changedProperties.get('product') as ProductCardData | ProductSmallCardData | undefined;
    if (
      _changedProperties.has('product') &&
      prevProduct?.kind === 'ProductCardData' &&
      this.product?.kind === 'ProductCardData' &&
      this.product.totalPrice !== prevProduct.totalPrice
    ) {
      const productKey = this.product.isbn13 || this.product.isbn || this.product.id;
      const liveEl = this.shadowRoot?.querySelector<HTMLElement>(`#price-live-region-${productKey}`);
      if (liveEl) {
        liveEl.textContent = `Product price updated to $${this.product.totalPrice?.toFixed(2)}`;
      }
    }
  }

  renderComp() {
    if (OfferTypeChecker.isCreateCustom(this.product?.offerType)) {
      return html`<mhecomm-create-custom-product-card-display
        .headingLevel="${this.headingLevel}"
        .product="${this.product}"
        .productIndex="${this.productIndex}"
        .store="${this.store}"
        .variation="${this.variation}"
        ?isCalledFromCartPreview=${this.isCalledFromCartPreview}
      ></mhecomm-create-custom-product-card-display>`;
    }

    if (this.variation === 'large-card') {
      if (this.view === 'largeResultCard') {
        return this.largeResultCard();
      } else if (this.view === 'card') {
        return this.largeCardCardView();
      } else if (this.view === 'list') {
        return this.largeCardListView();
      } else if (selectSelectedCatalog(store.getState() as StoreState)) {
        return this._selectedCatalogCard();
      } else {
        return this.largeCard();
      }
    } else {
      return this.smallCard();
    }
  }

  private _selectedCatalogCard() {
    if (this.product?.businessUnit?.toLocaleLowerCase() === PROFESSIONAL_LOWER) {
      return this._professionalCardSearchResults();
    } else if (this.product?.documentType === 'Products') {
      return this.largeCard(true);
    } else {
      return this._contentCard();
    }
  }

  /**
   * in this function find and highlighting the search keyword
   * @returns highlighted search query terms html
   */
  private _getHighlightedSearchTerms(id, text) {
    const searchTerms = selectRequest(store.getState() as StoreState)?.searchTerms?.trim();
    let htmlString: string, newElement: HTMLElement = document?.createElement('span');

    if (searchTerms && text) {
      const searchTermsGroups = searchTerms
        .split(' ')
        .map((v) => `(${escapeRegExp(v)})`)
        .join('|');
      const regex = new RegExp(searchTermsGroups, 'gi');
      htmlString =
        '<span>' + text.replace(regex, (searchTerm) => `<span class='fw-bold'>${searchTerm}</span>`) + '<span>';
      newElement.innerHTML = htmlString;
      return html`${newElement}`;
    }

    if (text && isTextContainsHTMLTags(text)) {
      htmlString = '<span>' + text + '<span>';
      newElement.setAttribute('id', id);
      newElement.innerHTML = htmlString;
      return html`${newElement}`;
    }

    return html`<span id="${id}">${text}</span>`;
  }

  /**
   * in this function find and highlighting the search keyword
   * @returns highlighted search query terms html
   */
  private _getHighlightedSearchV2Terms(id, text) {
    const searchTerms = selectV2Request(store.getState() as StoreState)?.searchTerms?.trim();
    if (searchTerms && text) {
      const searchTermsGroups = searchTerms
        .split(' ')
        .map((v) => `(${escapeRegExp(v)})`)
        .join('|');
      const regex = new RegExp(searchTermsGroups, 'gi');
      const htmlString =
        '<span>' + text.replace(regex, (searchTerm) => `<span class='fw-bold'>${searchTerm}</span>`) + '<span>';
      const newElement = document?.createElement('span');
      newElement.innerHTML = htmlString;
      return html`${newElement}`;
    }
    return html`<span id="${id}">${text} </span>`;
  }

  isMhpProduct() {
    return OfferTypeChecker.isMhpPrint(this.product?.offerType)
      || OfferTypeChecker.isMhpEbook(this.product?.offerType);
  }

  getProductFormat() {
    let format = '';
    if (isSharpenPlus(this.product?.programSourceId)) {
      format = 'Virtual';
    } else if (isTunnelPurchaseFlow() && isConnectProduct(this.product)) {
      format = 'Connect';
    } else {
      format = this.product?.format ?? '';
    }

    return html`<div>
      ${format ? html`<strong>Format: </strong><span>${format}</span>` : html``}
    </div>`;
  }

  largeCard(showSchoolPricing = false) {
    const showQuantity =
      this.product?.kind === 'ProductCardData'
        ? ['seg', 'seg-store', 'seg-product'].includes(this.product?.businessUnit || '')
        : false;
    const productMhidIsbn = this._isCartCardSegProduct() ? this.product?.mhid : this.product?.isbn;
    return this.product && this.product.kind === 'ProductCardData'
      ? html`
          ${this.isCartCard
          ? html`<hr class="mt-1" aria-hidden="true" />
                ${this.product?.isFreeTrial ? this.renderFreeTrialAvailabilityVerbiage(this.product) : nothing}
                ${displayFreeTrialMessages(this.product)}
                ${cardAlert('large-cart-error-message', this.product, AlertSize.small)} `
          : nothing}
          <div class="card ${this.isCartCard ? 'mb-3 border-lg-0' : 'shadow rounded border-0 mb-4'}">
            <div class="row card-body ${this.isCartCard ? 'p-lg-0' : ''}">
              ${this.product.businessUnit === 'PROFESSIONAL' && !this.isMhpProduct()
          ? this.professionalCard(this.product)
          : html`${!this.hideCoverImage 
                  ? html`
                    <div class="col-4 col-sm-3 col-md-2 col-lg-2 d-flex align-items-start justify-content-center">
                        <img
                          src="${this.product.image}"
                          onerror="this.src='${getImageSrcUrl(this.product.isbn!)}';"
                          class="img-fluid object-fit-contain"
                          alt=""
                        />
                    </div>`
                  : nothing}
                    <div class=${this.hideCoverImage ? "col-12 col-md-7 col-lg-8" : this.isCartCard ? "col-8 col-sm-9 col-md-5 col-xl-6" : "col-8 col-sm-9 col-lg-7"}>
                      ${this.product.catalogLabel
              ? html`<div class="fw-semibold mb-2">${this.product.catalogLabel}</div>`
              : nothing}
                      <h3 class="h5">
                        ${isTunnelPurchaseFlow() && this.isCartCard ? 
                          html`
                            <span id="${this.getProductTitleId()}" class="fs-large fw-semibold">
                              ${this._getHighlightedSearchTerms('heading-product-title', this.product?.title)}
                            </span>`
                        : html`
                          <a
                            id="${this.getProductTitleId()}"
                            href="${this.product.url}"
                            @click=${(e: Event) => this.dataLayerProductDetail(e)}
                            @auxclick=${(e: Event) => this.onAuxClick(e)}
                            @contextmenu=${() => this.onContextMenu()}
                            class="fs-large fw-semibold"
                            >
                              ${this._getHighlightedSearchTerms('heading-product-title', this.product?.title)}
                          </a>`
                        }
                      </h3>
                      <div>
                        <strong>
                          ${this.isCartCard && this.product.businessUnit != 'SEG' ? 'ISBN10' : 'MHID'}:&nbsp;</strong
                        >
                        ${productMhidIsbn} ${this.product.isbn13 ? html`| <strong>ISBN13: </strong> ${this.product.isbn13}` : nothing} 
                      </div>
                      ${!isSharpenPlus(this.product.programSourceId) && this.product.contributors
              ? html` <div>
                            <strong>By:</strong> ${this.formatContributors(this.product.contributors!)}
                          </div>`
              : nothing}
                      ${this.product.gradeLevels
              ? html`<div><strong>Grades: </strong> ${this.product.gradeLevels}</div>`
              : nothing}
                      ${this.product.publicationDate
              ? html`<strong>Published: </strong>${formatDateDefault(
                this.product.publicationDate
              )}`
              : nothing}
                      ${!isSharpenPlus(this.product.programSourceId) &&
              this.product.copyRightYear !== '' &&
              !this.product.isEvergreen
              ? html` <div><span>&#169;</span>${this.product.copyRightYear}</div>`
              : nothing}
                      ${this.getProductFormat()}
                      ${isSharpenPlus(this.product.programSourceId)
              ? html` <div class="${!isSharpenPlus(this.product.programSourceId) ? 'mt-2' : ''}">
                  <strong>Access Until:</strong>
                  ${calculateDurationDigitalProduct(
                    Number(this.product.duration),
                    this.product.durationPeriod ?? '',
                    new Date().getTime()
                  ).toLocaleDateString('en-US', {
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                  })}
                </div>`
              : nothing}
                      <div class="mt-2">
                        <mhecomm-my-bookshelf-redeem-link .product="${this.product}"></mhecomm-my-bookshelf-redeem-link>
                        ${this.product!.isVirtual === false
              ? html`<div>
                              <span class="badge bg-light text-dark">This product will be shipped to you.</span>
                            </div>`
              : html``}
                      </div>
                    </div>`}
              <div class=${this.hideCoverImage ? "col-12 col-md-5 col-lg-4" : this.isCartCard ? "col-12 col-md-5 col-xl-4" : "col-12 col-lg-3"}>
                ${this.isCartCard
          ? html`
                      <hr class="hr-primary d-md-none" aria-hidden="true" />
                      <div class="row justify-content-end">
                        <div
                          class=${this.product?.isFreeTrial ? 'col-12 col-sm-12 col-md-12 col-lg-12' : 'col-12 col-sm-12 col-md-12 col-lg-12'}
                        >
                          <div class="row pb-lg-1">
                            ${this.product.isDiscountedBundle ? renderOriginalPrice(this.product, productMhidIsbn) : nothing}
                            ${renderPrice(this.product, productMhidIsbn)}
                            ${renderItemPrice(this.product, productMhidIsbn)}
                            ${this.product?.isFreeTrial
              ? html`
                                  <div
                                    class="d-flex justify-content-end text-end"
                                  >
                                    <div class="col-6">
                                      <mhecomm-free-trial-subscription-info
                                        .freeTrial="${mapProductToFreeTrialInfo(this.product)}"
                                        ?showCancellationInfo=${true}
                                        colSize="col-12"
                                        variant="large"
                                      />
                                    </div>
                                  </div>
                                `
              : ''}
                          </div>
                        </div>
                        <div
                          class=${this.product?.isFreeTrial
              ? 'col-4 col-lg-12 d-flex align-items-end justify-content-end'
              : 'col-6 col-sm-12'}
                        >
                          <mhecomm-add-to-cart
                            ?showRemoveLink=${true}
                            ?showQuantity=${this.isProfessionalOrLooseleafDiscounted(this.product) ? false : true}
                            ?isVirtual=${this.product.isVirtual}
                            quantity=${this.product.quantity!}
                            businessUnit=${this.product.businessUnit}
                            store=${ifDefined(this.store)}
                            productTitle=${this.product.title}
                            isbn=${this.product.isbn || this.product.mhid!}
                            isbn13=${this.product.isbn13!}
                            ?showSchoolPricing=${showSchoolPricing}
                            format=${ifDefined(this.product?.format)}
                            .programSourceId=${this.product?.programSourceId}
                            offerPricingId=${this.product?.offerPricingId ?? ""}
                            offerType=${this.product?.offerType ?? ""}
                            .productIndex=${this.productIndex! + 1 || 1}
                            .durationType=${this.product?.durationType}
                            .edition=${this.product?.edition}
                            .editionOrRelease=${this.product?.editionOrRelease}
                            .isEvergreenProduct=${this.product?.isEvergreen}
                          >
                          </mhecomm-add-to-cart>
                        </div>
                      </div>
                    `
          : html`
                      <mhecomm-add-to-cart
                        text=${this.text}
                        updateText=${showQuantity ? 'Update cart' : 'Remove from cart'}
                        netPrice=${showQuantity ? this.product.netPrice : ''}
                        ?showQuantity=${showQuantity}
                        businessUnit=${this.product.businessUnit}
                        store=${ifDefined(this.product.store)}
                        ?showRestrictions=${false}
                        ?showComingSoon=${this.product.isPublished}
                        isbn=${this.product.isbn || this.product.mhid!}
                        isbn13=${this.product.isbn13!}
                        ?showSchoolPricing=${showSchoolPricing}
                        .productTitle=${this.product.title}
                        .businessUnit=${this.product.businessUnit}
                        .copyRightYear=${this.product.copyRightYear}
                        .description=${this.product.description}
                        .gradeLevel=${this.product.gradeLevels}
                        .contributors=${formattedJoin(this.product.contributors!)}
                        .edition=${this.product.edition}
                        .editionOrRelease=${this.product.editionOrRelease}
                        .programSourceId=${this.product?.programSourceId}
                        offerPricingId=${this.product?.offerPricingId ?? ""}
                        .productIndex=${this.productIndex! + 1 || 1}
                      >
                      </mhecomm-add-to-cart>
                    `}
              </div>
            </div>
          </div>
        `
      : html``;
  }

  private isProfessionalOrLooseleafDiscounted(product: ProductCardData) {
    return (
      (product.businessUnit === 'PROFESSIONAL' && !OfferTypeChecker.isMhpPrint(product?.offerType)) ||
      OfferTypeChecker.isConnectLLDiscounted(product?.offerType)
    );
  }

  private professionalCard(product) {
    return html` <div class="col-4 col-sm-3 col-md-2 d-flex align-items-center justify-content-center">
        <a
          tabindex="-1"
          href="${product.url}"
          @click=${(e: Event) => this.dataLayerProductDetail(e)}
          @auxclick=${(e: Event) => this.onAuxClick(e)}
          @contextmenu=${() => this.onContextMenu()}
        >
          <img src="${product.image}" class="img-fluid" alt=${product.title} />
        </a>
      </div>
      <div class="col-8 col-sm-9 col-md-5 col-xl-6">
        <h3 class="h5">
          <a
            id="product-title-${product.isbn}"
            href="${product.url}"
            @click=${(e: Event) => this.dataLayerProductDetail(e)}
            @auxclick=${(e: Event) => this.onAuxClick(e)}
            @contextmenu=${() => this.onContextMenu()}
            class="fs-large fw-semibold"
          >
            ${this._getHighlightedSearchTerms('heading-product-title', product.title)}
          </a>
        </h3>
        ${hasAuthorInfo(product)
        ? html`<div><strong>By:</strong> ${this.formatContributors(product.contributors!)}</div>`
        : nothing}
        <div>
          ${product.format ? html`<strong>Format: </strong><span>${product.format}</span>` : html``}
        </div>
        <div>
          <mhecomm-subscription-length labelSize="large" .product="${product}" />
        </div>
        <div>
          <mhecomm-subscription-access-through .product="${product}" />
        </div>
        <div>
          <mhecomm-free-trial-end-date .freeTrial="${mapProductToFreeTrialInfo(product)}" />
        </div>
      </div>`;
  }

  private _professionalCardSearchResults() {
    return this.product && this.product.kind !== 'ProductSmallCardData'
      ? html`
          <div class="card shadow rounded border-0 mb-4">
            <div class="row card-body">
              <div class="col-4 col-sm-3 col-md-2 col-lg-2 d-flex align-items-top justify-content-center">
                <a
                  tabindex="-1"
                  href="${this.product.url}"
                  @click=${(e: Event) => this.dataLayerProductDetail(e)}
                  @auxclick=${(e: Event) => this.onAuxClick(e)}
                  @contextmenu=${() => this.onContextMenu()}
                >
                  <img src="${this.product.image}" class="img-fluid" alt="" />
                </a>
              </div>
              <div class="col-8 col-sm-9 col-md-10 col-lg-10">
                ${this.product.catalogLabel
          ? html`<div class="fw-semibold mb-2">${this.product.catalogLabel}</div>`
          : nothing}
                <h3 class="h5">
                  <a
                    id="${this.getProductTitleId()}"
                    href="${this.product.url}"
                    @click=${(e: Event) => this.dataLayerProductDetail(e)}
                    @auxclick=${(e: Event) => this.onAuxClick(e)}
                    @contextmenu=${() => this.onContextMenu()}
                    class="fs-large fw-semibold"
                  >
                    ${this._getHighlightedSearchTerms('heading-product-title', this.product?.title)}
                  </a>
                </h3>
                ${this.product.contributors
          ? html`<div>
                      <strong>By:</strong> ${this.formatContributors(this.product.contributors!)}
                    </div>`
          : nothing}
                <div>
                  <p class="d-none d-lg-block my-1">
                    ${this._getHighlightedSearchTerms(
            'product-description',
            truncatingCleanString(this.product.description)
          )}
                  </p>
                  <a
                    href="${this.product.url}"
                    aria-describedby="${this.getProductTitleId()}"
                    @click=${(e: Event) => this.dataLayerProductDetail(e)}
                    @auxclick=${(e: Event) => this.onAuxClick(e)}
                    @contextmenu=${() => this.onContextMenu()}
                  >
                    View Details&nbsp;<uxt-icon icon="chevron-right" size="md"></uxt-icon>
                  </a>
                </div>
              </div>
            </div>
          </div>
        `
      : nothing;
  }

  private _contentCard() {
    return this.product && this.product.kind !== 'ProductSmallCardData'
      ? html`
          <div class="card shadow rounded border-0 mb-4">
            <div class="row card-body">
              <div class="col-4 col-sm-3 col-md-3 col-lg-2 d-flex align-items-start justify-content-center">
                  <img src="${this.product.image}" class="img-fluid object-fit-contain" alt="" />
              </div>
              <div class="col-8 col-sm-9 col-md-9 col-lg-10">
                ${this.product.catalogLabel
          ? html`<div class="fw-semibold mb-2">${this.product.catalogLabel}</div>`
          : nothing}
                <h3 class="h5">
                  <a
                    id="${this.getProductTitleId()}"
                    href="${this.product.url}"
                    @click=${(e: Event) => this.dataLayerProductDetail(e)}
                    @auxclick=${(e: Event) => this.onAuxClick(e)}
                    @contextmenu=${() => this.onContextMenu()}
                    class="fs-large fw-semibold"
                  >
                    ${this._getHighlightedSearchTerms('heading-product-title', this.product?.title)}
                  </a>
                  ${this.product.edition
          ? html`<p class="fs-6 mb-2">${ordinalSuffixOf(this.product.edition)} Edition</p>`
          : nothing}
                </h3>
                ${this.product.contributors
          ? html`<div>
                      <strong>By:</strong> ${this.formatContributors(this.product.contributors!)}
                    </div>`
          : nothing}
                ${this.product.publicationDate
          ? html`<div>
                      <strong>Published: </strong>${formatDateDefault(this.product.publicationDate)}
                    </div>`
          : nothing}
                ${!isSharpenPlus(this.product.programSourceId) &&
          this.product.copyRightYear !== '' &&
          !this.product.isEvergreen
          ? html` <div><span>&#169;</span>${this.product.copyRightYear}</div>`
          : nothing}
                <div>
                  <p class="d-none d-lg-block my-1">
                    ${this._getHighlightedSearchTerms(
            'product-description',
            truncatingCleanString(this.product.description)
          )}
                  </p>
                  <a
                    href="${this.product.url}"
                    aria-describedby="${this.getProductTitleId()}"
                    @click=${(e: Event) => this.dataLayerProductDetail(e)}  
                    @auxclick=${(e: Event) => this.onAuxClick(e)}
                    @contextmenu=${() => this.onContextMenu()}
                  >
                    View Details&nbsp;<uxt-icon icon="chevron-right" size="md"></uxt-icon>
                  </a>
                </div>
              </div>
            </div>
          </div>
        `
      : html``;
  }

  largeCardCardView() {
    const showQuantity =
      this.product?.kind === 'ProductCardData'
        ? ['seg', 'seg-store', 'seg-product'].includes(this.product?.businessUnit || '')
        : false;
    const productMhidIsbn = this._isCartCardSegProduct() ? this.product?.mhid : this.product?.isbn;
    return this.product && this.product.kind === 'ProductCardData'
      ? html`
          ${this.isCartCard
          ? html`<hr class="mt-1" aria-hidden="true" />
                ${this.product.errorMessage
              ? html`${cardAlert('large-cart-error-message', this.product, AlertSize.small)}`
              : nothing} `
          : nothing}

          <div class="card ${this.isCartCard ? 'border-lg-0' : ''}">
            <div class="row card-body ${this.isCartCard ? 'p-lg-0' : ''}">
              <div class="col-4 col-sm-3 d-flex align-items-center justify-content-center">
                <a
                  tabindex="-1"
                  href="${this.product.url}"
                  @click=${(e: Event) => this.dataLayerProductDetail(e)}
                  @auxclick=${(e: Event) => this.onAuxClick(e)}
                  @contextmenu=${() => this.onContextMenu()}
                >
                  <img src="${this.product.image}" class="img-fluid" alt=${this.product.title} />
                </a>
              </div>
              <div class="col-8 col-sm-9">
                <h3 class="h5">
                  <a
                    id="${this.getProductTitleId()}"
                    href="${this.product.url}"
                    @click=${(e: Event) => this.dataLayerProductDetail(e)}
                    @auxclick=${(e: Event) => this.onAuxClick(e)}
                    @contextmenu=${() => this.onContextMenu()}
                    class="fs-6 fw-bold"
                  >
                    <span>${(this.headingLevel, this.product.title)} </span>
                  </a>
                </h3>
                ${productMhidIsbn
          ? html`
                      <div>
                        <span class="fw-semibold">
                          ${this.isCartCard && this.product.businessUnit != 'SEG' ? 'ISBN10' : 'MHID'}:&nbsp;</span
                        >
                        ${productMhidIsbn}
                      </div>
                    `
          : nothing}
                ${this.product.isbn13
          ? html` <div><span class="fw-semibold">ISBN13: </span> ${this.product.isbn13}</div> `
          : nothing}

                <div class="mt-2">
                  <mhecomm-my-bookshelf-redeem-link .product="${this.product}"></mhecomm-my-bookshelf-redeem-link>
                  ${this.product!.isVirtual === false
          ? html`<div>
                        <span class="badge bg-light text-dark">This product will be shipped to you.</span>
                      </div>`
          : html``}
                </div>
              </div>
              <div class="col-12 col-sm-12">
                ${this.isCartCard
          ? html`
                      <hr class="hr-primary d-lg-none" aria-hidden="true" />
                      <div class="row">
                        <div class="col-6 col-sm-3 col-md-3 col-lg-12">
                          <div class="row pb-3">
                            ${this.product.isDiscountedBundle ? renderOriginalPrice(this.product, productMhidIsbn) : nothing}
                            ${renderPrice(this.product, productMhidIsbn)}
                            ${renderItemPrice(this.product, productMhidIsbn)}
                          </div>
                        </div>
                        <div class="col-6 col-sm-9 col-md-12 col-lg-12">
                          <mhecomm-add-to-cart
                            ?showRemoveLink=${true}
                            ?showQuantity=${true}
                            ?isVirtual=${this.product.isVirtual}
                            quantity=${this.product.quantity!}
                            businessUnit=${this.product.businessUnit}
                            store=${ifDefined(this.store)}
                            productTitle=${this.product.title}
                            isbn=${this.product.isbn || this.product.mhid!}
                            isbn13=${this.product.isbn13 === 'N/A' ? '' : this.product.isbn13!}
                            format=${ifDefined(this.product?.format)}
                            .programSourceId=${this.product?.programSourceId}
                            offerPricingId=${this.product?.offerPricingId ?? ""}
                            offerType=${this.product?.offerType ?? ""}
                            .productIndex=${this.productIndex! + 1 || 1}
                            .durationType=${this.product?.durationType}
                            .edition=${this.product?.edition}
                            .editionOrRelease=${this.product?.editionOrRelease}
                            .isEvergreenProduct=${this.product?.isEvergreen}
                          >
                          </mhecomm-add-to-cart>
                        </div>
                      </div>
                    `
          : html`
                      <mhecomm-add-to-cart
                        text=${this.text}
                        updateText=${showQuantity ? 'Update cart' : 'Remove from cart'}
                        netPrice=${showQuantity ? this.product.netPrice : ''}
                        ?showQuantity=${showQuantity}
                        businessUnit=${this.product.businessUnit}
                        store=${ifDefined(this.product.store)}
                        ?showRestrictions=${false}
                        ?showComingSoon=${false}
                        isbn=${this.product.isbn || this.product.mhid!}
                        isbn13=${this.product.isbn13 === 'N/A' ? '' : this.product.isbn13!}
                        .productTitle=${this.product.title}
                        .businessUnit=${this.product.businessUnit}
                        .copyRightYear=${this.product.copyRightYear}
                        .description=${this.product.description}
                        .gradeLevel=${this.product.gradeLevels}
                        .contributors=${formattedJoin(this.product.contributors!)}
                        .edition=${this.product.edition}
                        .editionOrRelease=${this.product.editionOrRelease}
                        view=${this.view}
                        .programSourceId=${this.product?.programSourceId}
                        offerPricingId=${this.product?.offerPricingId ?? ""}
                        .productIndex=${this.productIndex! + 1 || 1}
                      >
                      </mhecomm-add-to-cart>
                    `}
              </div>
            </div>
          </div>
        `
      : html``;
  }

  private renderFreeTrialAvailabilityVerbiage(product: ProductCardData) {
    if (isCustomerLoginIdleOrLoading(store.getState() as StoreState)) {
      return nothing;
    }

    const isLoggedIn = isCustomerLoginSuccess(store.getState() as StoreState);
    const freeTrialDuration = getFreeTrialDuration(product?.freeTrialStartDate, product?.freeTrialEndDate);

    return html`
      <div class="my-3">
        ${isLoggedIn
        ? html` <mhecomm-signedin-availability-free-trial
              .freeTrialDuration=${freeTrialDuration}
              .isbn=${product.isbn!}
              .product=${product}
              ?isCartList=${true}
            ></mhecomm-signedin-availability-free-trial>`
        : html` <mhecomm-signedout-availability-free-trial
              .freeTrialDuration=${freeTrialDuration}
              .isbn=${product.isbn!}
              .programId=${product.programSourceId!}
              ?isCartList=${true}
            >
            </mhecomm-signedout-availability-free-trial>`}
      </div>
    `;
  }

  largeCardListView() {
    const showQuantity =
      this.product?.kind === 'ProductCardData'
        ? ['seg', 'seg-store', 'seg-product'].includes(this.product?.businessUnit || '')
        : false;
    const productMhidIsbn = this._isCartCardSegProduct() ? this.product?.mhid : this.product?.isbn;
    return this.product && this.product.kind === 'ProductCardData'
      ? html`
          <div class="row py-3 border-bottom">
            <div class="col-12 col-md-5 col-lg-7">
              <div class="row">
                <div class="col-4 d-md-none">
                  <div class="fw-semibold">Title:</div>
                </div>
                <div class="col-8 col-md-12">
                  <h3 class="h5">
                    <a
                      id="${this.getProductTitleId()}"
                      href="${this.product.url}"
                      @click=${(e: Event) => this.dataLayerProductDetail(e)}
                      @auxclick=${(e: Event) => this.onAuxClick(e)}
                      @contextmenu=${() => this.onContextMenu()}
                      class="fs-6 fw-semibold"
                    >
                      <span>${(this.headingLevel, this.product.title)} </span>
                    </a>
                  </h3>
                  <div>
                    ${productMhidIsbn
          ? html`
                          <span class="fw-semibold">
                            ${this.isCartCard && this.product.businessUnit != 'SEG' ? 'ISBN10' : 'MHID'}:&nbsp;</span
                          >
                          ${productMhidIsbn}
                        `
          : nothing}
                    ${this.product.isbn13
          ? html`
                          <span class="d-none d-md-inline">|</span>
                          <span class="d-block d-md-inline">
                            <span class="fw-semibold">ISBN13:</span>
                            ${this.product.isbn13}
                          </span>
                        `
          : nothing}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-7 col-lg-5 mt-2 mt-md-0">
              <mhecomm-add-to-cart
                text=${this.text}
                updateText=${showQuantity ? 'Update cart' : 'Remove from cart'}
                netPrice=${showQuantity ? this.product.netPrice : ''}
                ?showQuantity=${showQuantity}
                businessUnit=${this.product.businessUnit}
                store=${ifDefined(this.product.store)}
                ?showRestrictions=${false}
                ?showComingSoon=${false}
                isbn=${this.product.isbn || this.product.mhid!}
                isbn13=${this.product.isbn13 === 'N/A' ? '' : this.product.isbn13!}
                .productTitle=${this.product.title}
                .businessUnit=${this.product.businessUnit}
                .copyRightYear=${this.product.copyRightYear}
                .description=${this.product.description}
                .gradeLevel=${this.product.gradeLevels}
                .contributors=${formattedJoin(this.product.contributors!)}
                .edition=${this.product.edition}
                .editionOrRelease=${this.product.editionOrRelease}
                view=${this.view}
                .programSourceId=${this.product?.programSourceId}
                offerPricingId=${this.product?.offerPricingId ?? ""}
                .productIndex=${this.productIndex! + 1 || 1}
              >
              </mhecomm-add-to-cart>
            </div>
          </div>
        `
      : html``;
  }

  _isCartCardSegProduct() {
    return this.isCartCard && this.product?.businessUnit == 'SEG';
  }

  getProductTitleId(): string {
    const productMhidIsbn = this._isCartCardSegProduct() ? this.product?.mhid : this.product?.isbn || this.product?.isbn13;
    return getSanitizedProductTitle(productMhidIsbn, this.product?.title);
  }

  formatContributors(contributors: string[]): string {
    return formattedJoin(contributors);
  }

  smallCard() {
    if (this.isCartCard) {
      return this.cartSmallCard();
    }
    return this.pdpSmallCard();
  }

  pdpSmallCard() {
    return renderPdpSmallCardTemplate({
      product: this.product,
      headingLevel: this.headingLevel,
      showFullTitle: this.showFullTitle,
      store: this.store,
      productIndex: this.productIndex,
      onShowTitle: (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        this.showFullTitle = true;
        emit({
          element: this,
          name: 'show-title',
          detail: dataLayerDetail('Show Title', this.product),
        });
      },
    });
  }

  cartSmallCard() {
    return this.product && this.product.kind === 'ProductCardData'
      ? html`
          ${cardAlert('small-cart-error-message', this.product, AlertSize.small)}
          <div class="d-flex row gx-2">
            ${!this.hideCoverImage 
              ? html`
                <div class="col-3 mb-3">
                  <img
                    class="img-fluid"
                    src="${this.errorImage ? this.errorImage : this.product.image}"
                    @error=${() => this.loadDefaultImage()}
                  />
                </div>`
              : nothing}
            ${this.product?.isFreeTrial
          ? html`
                  <div class="col-9 d-flex flex-column">
                    ${heading(this.headingLevel, this.product.title, ['fs-6', 'fw-semibold', 'mb-1'])}
                    <div class="mb-1">
                      <mhecomm-subscription-length .product="${this.product}" />
                      ${renderQuantity(this.product)}
                    </div>
                    <mhecomm-free-trial-end-date .freeTrial="${mapProductToFreeTrialInfo(this.product)}" />
                  </div>
                  <div>
                    <div 
                      class="d-flex flex-row justify-content-between" 
                      aria-live="${ifDefined(this.isCartCard ? 'polite' : undefined)}" 
                      aria-atomic="${ifDefined(this.isCartCard ? 'true' : undefined)}"
                    >
                      <span>Price:</span>
                      <span class="fw-bold fs-large"> $${this.product.totalPrice?.toFixed(2)} </span>
                    </div>
                    <mhecomm-free-trial-subscription-info 
                      .freeTrial="${mapProductToFreeTrialInfo(this.product)}" 
                      ?showCancellationInfo=${true}
                      ?showCancellationTerms=${true}
                    />
                  </div>
                `
          : html`
                  <div class="col-9">
                    ${heading(this.headingLevel, getProductTitleWithoutEdition({
                      title: this.product.title,
                      programSourceId: this.product?.programSourceId!,
                      businessUnit: this.product.businessUnit,
                      isVirtual: this.product.isVirtual ? true : false,
                      formatTypeDesc: this.product?.formatTypeDesc!,
                      duration: this.product?.duration!,
                      durationPeriod: this.product?.durationPeriod!,
                      ...(this.product?.offerType && {offerType: this.product?.offerType}),
                    }), [
            'fs-6',
            'fw-semibold',
            'mb-1',
          ])}
                    ${!isSharpenPlus(this.product.programSourceId)
              ? html` <mhecomm-subscription-length .product="${this.product}" />
                          ${renderQuantity(this.product)}`
              : nothing}
                    ${isSharpenPlus(this.product.programSourceId)
              ? html` <div class="fw-semibold fs-6">
                          Access through:
                          <span class="fw-normal"
                            >${calculateDurationDigitalProduct(
                Number(this.product.duration),
                this.product.durationPeriod ?? '',
                new Date().getTime()
              ).toLocaleDateString('en-US')}</span
                          >
                        </div>`
              : nothing}
                    ${isRedeemableProduct(this.product)
              ? html`
                  <span class="fw-bold">
                    Access Until:
                  </span>
                  <mhecomm-date-label
                    .duration='${this.product.duration + ""}'
                    .period='${this.product.durationPeriod}'
                    ?applyStyles=${true}
                  ></mhecomm-date-label>
                `
              : nothing}
                  </div>
                  <div class="col-12 d-flex flex-column justify-content-between ps-1">
                      ${this.product.isDiscountedBundle ? html`
                        <div class="d-flex justify-content-between">
                          <span>Original Price:</span>
                          <span class="fw-bold fs-large text-decoration-line-through">$${(parseFloat(this.product.listPrice!.toFixed(2)) * this.product.quantity!).toFixed(2)}</span>
                        </div>`
              : nothing
            }
                      <div class="d-flex justify-content-between">
                      <span>${this.product.isDiscountedBundle ? 'Bundle Offer:' : 'Price:'}</span>
                      <span class="fw-bold fs-large">$${this.product.totalPrice?.toFixed(2)}</span>
                      </div>
                  </div>
                `}
          </div>
        `
      : html``;
  }

  largeResultCard() {
    return this.searchResult !== undefined
      ? html`
          <div class="card shadow rounded border-0 mb-4">
            <div class="row card-body">
              <div class="col-3 col-sm-3 col-md-2 col-lg-2 d-flex align-items-center justify-content-center">
                <div>
                  ${assignmentIcon}
                </div>
              </div>
              <div class="col-9 col-sm-9 col-md-9 col-lg-9">
                <h2 id="support-page-title">
                  <a
                    id="support-page-title-${this.searchResult.id}"
                    href="${this.searchResult.url}"
                    @click=${() => this.dataLayerSearchResultClick()}
                    class="fs-large fw-semibold"
                    target="_blank"
                    aria-label="${ariaLabelWithNewTab(this.searchResult?.title)}"
                    tabindex="0"
                  >
                    ${this._getHighlightedSearchV2Terms('heading-support-page-title', this.searchResult?.title)} &nbsp;
                    <!-- Inline SVG (not <uxt-icon>): uxt-icon is a light-DOM custom element that
                         rewrites its own innerHTML on upgrade, which shifts the node Lit binds the
                         next part to and caused the description below to be injected into the icon
                         instead of its <p> (WECOOL-52335). A static SVG keeps Lit's nodes stable. -->
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="0.75em"
                      height="0.75em"
                      fill="currentColor"
                      class="mh-icon align-bottom lh-base"
                      viewBox="0 0 16 16"
                      aria-hidden="true"
                    >
                      <path fill-rule="evenodd" d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"></path>
                      <path fill-rule="evenodd" d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"></path>
                    </svg>
                  </a>
                </h2>
                <div>
                  <p class="d-none d-lg-block my-1">
                    ${truncatingCleanString(this.searchResult.body ?? '')}
                  </p>
                </div>
                <div>
                  ${(() => {
          const hasLearningLevel = this.searchResult.learning_level && this.searchResult.learning_level.length > 0;
          const hasProduct = this.searchResult.product && this.searchResult.product.length > 0;

          return html`
                      ${hasLearningLevel
              ? html`
                            <span class="d-block d-md-inline">
                              ${facetsMapping.learning_level}${this.searchResult.learning_level.length > 1 ? 's' : ''} :
                              <span class="fw-semibold mb-2"
                                >${getNbspFacetLabels(this.searchResult.learning_level).join(', ')}</span
                              >
                            </span>
                          `
              : nothing}
                      
                      ${hasLearningLevel && hasProduct
              ? html`<span class="d-none d-md-inline">|</span>`
              : nothing}
                      
                      ${hasProduct
              ? html`
                            <span class="d-block d-md-inline">
                              ${facetsMapping.product}${this.searchResult.product.length > 1 ? 's' : ''}:
                              <span class="fw-semibold mb-2"
                                >${getNbspFacetLabels(this.searchResult.product).join(', ')}</span
                              >
                            </span>
                          `
              : nothing}
                    `;
        })()}
                </div>
              </div>
            </div>
          </div>
        `
      : html``;
  }

  loadDefaultImage() {
    this.errorImage = DEFAULT_COVER_IMAGE;
  }

  private dataLayerProductDetail(event: Event) {
    this.trackSearchResultClickthrough();
    populatePdpLocalStorageObject(
      event as MouseEvent,
      this,
      this.productIndex!,
      this.product as ProductCardData,
      this.store
    );
  }

  private onAuxClick(event: Event) {
    const mouseEvent = event as MouseEvent;
    // Track middle-click (button === 1) as "open in new tab" action
    if (mouseEvent.button === 1 && this.isSearchResults) {
      this.trackSearchResultClickthrough();
    }
  }

  private onContextMenu() {
    this.trackSearchResultClickthrough();
  }

  private trackSearchResultClickthrough() {
    if (this.isSearchResults) {
      const productInfo = this.product as ProductCardData;
      const clickthroughId = productInfo?.id ?? productInfo?.programSourceId;
      if (clickthroughId) {
        trackSearchClickthrough(clickthroughId, productInfo.productBusinessUnit ?? productInfo.businessUnit);
      }
    }
  }

  private normalizeAnalyticsValue(value?: string | Array<string>) {
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    return value ?? '';
  }

  private async dataLayerSearchResultClick(label = 'Search Result Click Details') {
    const state = store.getState() as StoreState;
    const resultData = this.searchResult as SearchResultLargeCardData;
    const activeFilters = selectV2Request(state)?.fieldValueFilter ?? {};
    const searchTerms = selectV2Request(state)?.searchTerms ?? '';
    const totalResults = selectTotal(state) ?? 0;
    const catalog = selectSelectedCatalog(state);
    
    await this.elasticAnalyticsService.postClickthroughAnalytics(resultData.id, state);
    emit({
      element: this,
      name: 'search-result-click-details',
      detail: {
        label: label,
        user: getUserDataForUserAnalytics(state),
        catalog: catalog,
        resultDetail: JSON.parse(
          JSON.stringify({
            title: resultData.title,
            id: resultData.id,
            businessUnit: resultData.businessUnit,
            description: resultData.body,
            learningLevel: this.normalizeAnalyticsValue(resultData.learning_level),
            product: resultData.product,
            url: resultData.url,
            documentType: resultData.documentType,
            ...(resultData.url?.includes('support') && {
              supportProducts: this.normalizeAnalyticsValue(
                activeFilters['product']?.length ? activeFilters['product'] : resultData.product
              ),
              supportSite: this.normalizeAnalyticsValue(
                resultData.created_by_division?.length
                  ? resultData.created_by_division
                  : activeFilters['created_by_division']
              ),
              personas: this.normalizeAnalyticsValue(
                resultData.user_type?.length ? resultData.user_type : activeFilters['user_type']
              ),
              tags: this.normalizeAnalyticsValue(
                resultData.tags?.length ? resultData.tags : activeFilters['tags']
              ),
            }),
          })
        ),
        searchText: {
          productDiscovery: 'search',
          searchAttempts: 1,
          searchTerms: [{
            searchTerm: searchTerms || '',
            totalSearchResults: totalResults,
          }],
          pageDetails: {
            pageNumber: 1,
            index: 1,
            currentPageLength: 10,
          },
        },
      },
    });
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-product-card-display': ProductCardDisplay;
  }
}
